import React from "react";
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  Stack,
  IconButton,
  Chip,
  Fade,
  Divider,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";
const PROFILE_ICON_COLORS = [
  "#2563EB",
  "#16A34A",
  "#D97706",
  "#7C3AED",
  "#DC2626",
  "#0891B2",
  "#BE123C",
  "#4F46E5",
];

function EntityProfile({
  open,
  onClose,
  entity,
  sections = [],
  dialogPaperSx = {},
}) {
  if (!entity) return null;

  const title =
    entity.name ||
    entity.project_name ||
    entity.contact_name ||
    entity.title ||
    entity.company_name ||
    "Profile";

  const subtitle =
    entity.company?.name ||
    entity.company ||
    entity.organization ||
    entity.parent_company ||
    entity.company_name ||
    null;

  const locationParts = [entity.city, entity.state, entity.country].filter(Boolean);
  const locationLine = locationParts.length > 0 ? locationParts.join(", ") : null;

  const renderValue = (value) => {
    if (value === null || value === undefined || value === "") return null;

    if (typeof value === "object" && !React.isValidElement(value)) {
      if (Array.isArray(value)) {
        return (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {value.map((v, i) => (
              <Chip
                key={i}
                label={typeof v === "object" ? v?.name || v?.title || JSON.stringify(v) : v}
                size="small"
                sx={{
                  fontSize: "0.9rem",
                  backgroundColor: "rgba(0,51,108,0.07)",
                  color: BRAND_BLUE,
                  fontWeight: 600,
                  border: "1px solid rgba(0,51,108,0.12)",
                }}
              />
            ))}
          </Stack>
        );
      }
      return value?.name || value?.title || null;
    }

    if (typeof value === "boolean") {
      return (
        <Chip
          label={value ? "Yes" : "No"}
          size="small"
          sx={{
            fontSize: "0.9rem",
            backgroundColor: value ? "rgba(100,131,21,0.12)" : "rgba(0,0,0,0.06)",
            color: value ? BRAND_GREEN : "#555",
            fontWeight: 600,
            border: `1px solid ${value ? "rgba(100,131,21,0.25)" : "rgba(0,0,0,0.1)"}`,
          }}
        />
      );
    }

    return value;
  };

  const reorderRows = (rows) => {
    const cityIndex = rows.findIndex((r) => r.label?.toLowerCase().includes("city"));
    const countyIndex = rows.findIndex((r) => r.label?.toLowerCase().includes("county"));
    if (cityIndex !== -1 && countyIndex !== -1) {
      const newRows = [...rows];
      const [countyRow] = newRows.splice(countyIndex, 1);
      newRows.splice(cityIndex + 1, 0, countyRow);
      return newRows;
    }
    return rows;
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose?.()}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Fade}
      transitionDuration={160}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          overflow: "hidden",
          backgroundColor: BRAND_BLUE,
          backgroundImage: "none",
          boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
          ...dialogPaperSx,
        },
      }}
    >
      {/* HEADER */}
      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 2.5,
          background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, #004080 100%)`,
          color: "#fff",
          position: "relative",
          borderTopLeftRadius: "16px",
          borderTopRightRadius: "16px",
        }}
      >
        <IconButton
          onClick={() => onClose?.()}
          size="small"
          sx={{
            position: "absolute",
            right: 14,
            top: 14,
            color: "rgba(255,255,255,0.7)",
            bgcolor: "rgba(255,255,255,0.08)",
            "&:hover": { bgcolor: "rgba(255,255,255,0.16)", color: "#fff" },
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>

        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "12px",
              bgcolor: "rgba(255,255,255,0.12)",
              border: "1px solid rgba(255,255,255,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <BusinessIcon sx={{ fontSize: 30, color: "rgba(255,255,255,0.9)" }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, lineHeight: 1.2, color: "#fff", pr: 4 }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography sx={{ fontSize: 14, color: "rgba(255,255,255,0.72)", mt: 0.3 }}>
                {typeof subtitle === "object" ? subtitle?.name || "—" : subtitle}
              </Typography>
            )}
            {locationLine && !subtitle && (
              <Stack direction="row" spacing={0.4} alignItems="center" sx={{ mt: 0.3 }}>
                <LocationOnIcon sx={{ fontSize: 15, color: "#d32f2f" }} />
                <Typography sx={{ fontSize: 14, color: "#fff" }}>
                  {locationLine}
                </Typography>
              </Stack>
            )}
          </Box>
        </Stack>
      </Box>

      {/* CONTENT */}
      <DialogContent sx={{ p: 0, bgcolor: "#fff" }}>
        <Box sx={{ px: 3, py: 2.5 }}>
          <Stack spacing={0} divider={<Divider sx={{ borderColor: "rgba(0,51,108,0.07)" }} />}>
            {sections.map((section, sIdx) => {
              const orderedRows = reorderRows(section.rows);
              const visibleRows = orderedRows.filter((r) => r.showIf !== false && r.value !== null && r.value !== undefined && r.value !== "");

              if (visibleRows.length === 0) return null;

              return (
                <Box key={sIdx} sx={{ py: 2.5 }}>
                  {section.title && (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                      <Box sx={{ width: 3, height: 16, borderRadius: 2, bgcolor: BRAND_GREEN, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1, color: "text.secondary" }}>
                        {section.title}
                      </Typography>
                    </Stack>
                  )}

                  <Stack spacing={0} divider={<Divider sx={{ borderColor: "rgba(0,0,0,0.04)" }} />}>
                    {visibleRows.map(({ icon, label, value, showIf = true, iconColor }, idx) => {
                      if (!showIf) return null;

                      let finalIcon = icon;
                      if (label?.toLowerCase().includes("county")) {
                        finalIcon = <LocationOnIcon />;
                      }

                      const rendered = renderValue(value);
                      if (rendered === null) return null;

                      const color = iconColor || PROFILE_ICON_COLORS[idx % PROFILE_ICON_COLORS.length];

                      return (
                        <Stack
                          key={idx}
                          direction="row"
                          spacing={1.5}
                          alignItems="flex-start"
                          sx={{ py: 1.5 }}
                        >
                          {finalIcon && (
                            <Box
                              sx={{
                                width: 38,
                                height: 38,
                                borderRadius: "8px",
                                bgcolor: "rgba(0,51,108,0.06)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                mt: 0.1,
                              }}
                            >
                              {React.cloneElement(finalIcon, {
                                sx: { fontSize: 21, color },
                              })}
                            </Box>
                          )}

                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            {label && (
                              <Typography
                                sx={{
                                  fontSize: "0.76rem",
                                  fontWeight: 600,
                                  textTransform: "uppercase",
                                  letterSpacing: 0.7,
                                  color: BRAND_GREEN,
                                  lineHeight: 1.3,
                                }}
                              >
                                {label}
                              </Typography>
                            )}
                            <Box
                              sx={{
                                mt: label ? 0.4 : 0,
                                color: BRAND_BLUE,
                                fontWeight: 700,
                                fontSize: "1rem",
                                lineHeight: 1.5,
                                wordBreak: "break-word",
                              }}
                            >
                              {rendered}
                            </Box>
                          </Box>
                        </Stack>
                      );
                    })}
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default EntityProfile;
